import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom'; // To read query params
import { MapPinIcon, EnvelopeIcon, PhoneIcon, ChatBubbleLeftEllipsisIcon, BuildingOffice2Icon, ChevronRightIcon } from '@heroicons/react/24/outline';

const PageHeader = ({ title, subtitle }) => (
  <div className="bg-figma-dark-bg-secondary py-16 md:py-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-figma-dark-text-primary animate-fade-in-up">{title}</h1>
      {subtitle && <p className="mt-4 text-lg text-figma-dark-text-secondary max-w-3xl mx-auto animate-fade-in-up" style={{animationDelay: '0.1s'}}>{subtitle}</p>}
    </div>
  </div>
);

const ContactPage = () => {
  const location = useLocation(); // For query params
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    interest: 'General Inquiry',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const interestParam = queryParams.get('interest');
    if (interestParam) {
      // Decode URI component and replace + with space for readability if it comes from URL params
      setFormData(prevState => ({ ...prevState, interest: decodeURIComponent(interestParam.replace(/\+/g, ' ')) }));
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(false);
    setError(null);
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
        setError('Please fill in all required fields: Name, Email, Subject, and Message.');
        return;
    }

    try {
      console.log("Form data submitted:", formData);
      // Replace with actual API call: await axios.post('/api/contact', formData);
      await new Promise(resolve => setTimeout(resolve, 1000)); 
      setIsSubmitted(true);
      setFormData({ name: '', email: '', company: '', phone: '', subject: '', message: '', interest: formData.interest }); // Keep selected interest
      setTimeout(() => setIsSubmitted(false), 5000); // Reset submission status after 5s
    } catch (err) {
      console.error("Contact form submission error:", err);
      setError('Sorry, there was an error submitting your message. Please try again later or contact us directly via email.');
    }
  };

  const contactDetails = [
    {
      icon: BuildingOffice2Icon,
      title: 'Global Headquarters',
      lines: ['Interdize Technologies.', 'City Center,', 'Gwalior City, MP 474005', 'India']
    },
    {
      icon: PhoneIcon,
      title: 'Connect by Phone',
      lines: ['Sales & Inquiries: +91 7217746050', 'Technical Support: +91 8878179296']
    },
    {
      icon: EnvelopeIcon,
      title: 'Reach Us Via Email',
      lines: ['General/Careers/Support: admin@interdizesolutions.com']
    },
  ];

  return (
    <div className="bg-figma-dark-bg-primary">
      <PageHeader 
        title="Get in Touch"
        subtitle="We're excited to hear from you! Whether you have a question about our services, a project proposal, or just want to connect, please don't hesitate to reach out."
      />

      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            {/* Contact Form */}
            <div className="md:col-span-7 lg:col-span-8 bg-figma-dark-bg-secondary p-8 sm:p-10 rounded-lg border border-figma-dark-border shadow-2xl animate-fade-in-up">
              <h2 className="text-2xl md:text-3xl font-semibold text-figma-dark-text-primary mb-8">Send Us a Message</h2>
              {isSubmitted && (
                <div className="mb-6 p-4 bg-figma-dark-accent/20 text-figma-dark-accent border border-figma-dark-accent/30 rounded-md text-sm">
                  Thank you! Your message has been sent successfully. We'll get back to you shortly.
                </div>
              )}
              {error && (
                <div className="mb-6 p-4 bg-red-500/10 text-red-400 border border-red-500/30 rounded-md text-sm">
                  {error}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-figma-dark-text-secondary mb-1">Full Name*</label>
                    <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} required className="w-full" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-figma-dark-text-secondary mb-1">Email Address*</label>
                    <input type="email" name="email" id="email" value={formData.email} onChange={handleChange} required className="w-full" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="company" className="block text-sm font-medium text-figma-dark-text-secondary mb-1">Company</label>
                        <input type="text" name="company" id="company" value={formData.company} onChange={handleChange} className="w-full" />
                    </div>
                    <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-figma-dark-text-secondary mb-1">Phone</label>
                        <input type="tel" name="phone" id="phone" value={formData.phone} onChange={handleChange} className="w-full" />
                    </div>
                </div>
                <div>
                  <label htmlFor="interest" className="block text-sm font-medium text-figma-dark-text-secondary mb-1">I'm interested in...</label>
                  <select id="interest" name="interest" value={formData.interest} onChange={handleChange} className="w-full">
                    <option>General Inquiry</option>
                    <option>AI Solutions</option>
                    <option>Robotics & Automation</option>
                    <option>Custom Software Development</option>
                    <option>Cloud Solutions</option>
                    <option>Data Analytics</option>
                    <option>Technology Consulting</option>
                    <option>Partnership Opportunities</option>
                    <option>Careers</option>
                    <option>Media Inquiry</option>
                    <option>Investor Relations</option>
                    <option>Support</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-figma-dark-text-secondary mb-1">Subject*</label>
                  <input type="text" name="subject" id="subject" value={formData.subject} onChange={handleChange} required className="w-full" />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-figma-dark-text-secondary mb-1">Message*</label>
                  <textarea name="message" id="message" rows="5" value={formData.message} onChange={handleChange} required className="w-full"></textarea>
                </div>
                <div>
                  <button 
                    type="submit" 
                    className="w-full sm:w-auto inline-flex items-center justify-center bg-figma-dark-primary text-white font-semibold px-8 py-3 rounded-lg text-base hover:bg-figma-dark-primary-hover transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-60"
                    disabled={isSubmitted} 
                  >
                    Send Message <ChevronRightIcon className="ml-2 h-5 w-5"/>
                  </button>
                </div>
              </form>
            </div>

            {/* Contact Information Side Panel - Figma Dark Style */}
            <div className="md:col-span-5 lg:col-span-4 space-y-8 animate-fade-in-up" style={{ animationDelay: '0.2s'}}>
              {contactDetails.map(detail => (
                <div key={detail.title} className="flex items-start p-4 bg-figma-dark-bg-secondary rounded-lg border border-figma-dark-border shadow-md">
                  <detail.icon className="flex-shrink-0 h-7 w-7 text-figma-dark-primary mr-4 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-figma-dark-text-primary mb-1">{detail.title}</h3>
                    {detail.lines.map(line => (
                        <p key={line} className="text-base text-figma-dark-text-secondary leading-relaxed">{line}</p>
                    ))}
                  </div>
                </div>
              ))}
               {/* Business Hours Section */}
               <div className="flex items-start p-4 bg-figma-dark-bg-secondary rounded-lg border border-figma-dark-border shadow-md">
                 <ChatBubbleLeftEllipsisIcon className="flex-shrink-0 h-7 w-7 text-figma-dark-primary mr-4 mt-1" /> 
                 <div>
                    <h3 className="text-lg font-semibold text-figma-dark-text-primary mb-1">Business Hours</h3>
                    <p className="text-base text-figma-dark-text-secondary leading-relaxed">Monday - Friday: 9:00 AM - 6:00 PM (IST)</p>
                 </div>
               </div>
            </div>
          </div>
        </div>
      </section>
      {/* Optional: Map Section - Figma Dark Style */}
      {/* <section className="bg-figma-dark-bg-tertiary">
        <div className="max-w-7xl mx-auto">
          <img src="https://placehold.co/1600x500/16161A/7F5AF0?text=Our+Location+Map&font=montserrat" alt="Map showing YourCompany location" className="w-full h-96 object-cover opacity-75" />
        </div>
      </section> */}
    </div>
  );
};

export default ContactPage;